import { Box } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';

export const LOADING_DIAMOND_SRC = '/animations/loading_diamond.json';
export const LOADING_DIAMOND_CANVAS_SIZE = 160;
export const LOADING_DIAMOND_LOTTIE_TEST_ID = 'loading-diamond-lottie';
export const LOADING_DIAMOND_FALLBACK_TEST_ID = 'loading-diamond-fallback';

type LoadingDiamondAnimationProps = {
  fallbackTestId?: string;
  lottieTestId?: string;
};

const DiamondFallback = ({ testId }: { testId: string }) => (
  <Box
    aria-hidden="true"
    data-testid={testId}
    h={`${LOADING_DIAMOND_CANVAS_SIZE}px`}
    position="relative"
    w={`${LOADING_DIAMOND_CANVAS_SIZE}px`}
  >
    <Box
      border="3px solid"
      borderColor="#005BBB"
      h="72px"
      left="50%"
      position="absolute"
      top="50%"
      transform="translate(-50%, -50%) rotate(45deg)"
      w="72px"
    />
    <Box
      bg="#E11D25"
      borderRadius="full"
      bottom="44px"
      h="18px"
      position="absolute"
      right="44px"
      w="18px"
    />
  </Box>
);

export function LoadingDiamondAnimation({
  fallbackTestId = LOADING_DIAMOND_FALLBACK_TEST_ID,
  lottieTestId = LOADING_DIAMOND_LOTTIE_TEST_ID,
}: LoadingDiamondAnimationProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [showFallback, setShowFallback] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || showFallback) return undefined;

    let isDisposed = false;
    let dotLottie: { destroy: () => void } | null = null;
    let rafId = 0;

    const waitForLayout = () =>
      new Promise<void>((resolve) => {
        rafId = window.requestAnimationFrame(() => {
          rafId = window.requestAnimationFrame(() => resolve());
        });
      });

    const loadAnimation = async () => {
      try {
        await waitForLayout();
        if (isDisposed) return;

        if (canvas.clientWidth <= 0 || canvas.clientHeight <= 0) {
          setShowFallback(true);
          return;
        }

        const { DotLottie } = await import('@lottiefiles/dotlottie-web');
        if (isDisposed) return;

        dotLottie = new DotLottie({
          autoplay: true,
          canvas,
          layout: {
            align: [0.5, 0.5],
            fit: 'contain',
          },
          loop: true,
          renderConfig: {
            autoResize: false,
            devicePixelRatio: 1,
          },
          src: LOADING_DIAMOND_SRC,
        });
      } catch {
        if (!isDisposed) {
          setShowFallback(true);
        }
      }
    };

    void loadAnimation();

    return () => {
      isDisposed = true;
      window.cancelAnimationFrame(rafId);
      try {
        dotLottie?.destroy();
      } catch {
        // DotLottie may throw during teardown after a WASM render error.
      }
    };
  }, [showFallback]);

  if (showFallback) {
    return <DiamondFallback testId={fallbackTestId} />;
  }

  return (
    <Box
      aria-hidden="true"
      h={`${LOADING_DIAMOND_CANVAS_SIZE}px`}
      w={`${LOADING_DIAMOND_CANVAS_SIZE}px`}
    >
      <canvas
        data-testid={lottieTestId}
        height={LOADING_DIAMOND_CANVAS_SIZE}
        ref={canvasRef}
        style={{ display: 'block', height: '100%', width: '100%' }}
        width={LOADING_DIAMOND_CANVAS_SIZE}
      />
    </Box>
  );
}
